import type { DestroyOptions } from 'pixi.js';
import { Graphics, Sprite, Container } from 'pixi.js';
import type { KnotBlockerModel } from 'game/boardMVC/models/blockers/KnotBlockerModel';
import type { XY } from 'utils/MathUtils';
import MathUtils from 'utils/MathUtils';
import { waitForMs } from 'utils/async-utils';
import { BOARD_SETTINGS } from 'game/scene/GameSceneConstants';
import { gridPositionToPxPosition } from 'game/boardMVC/utils/gridPositionToPxPosition';
import type { ConnectedBlockerView, GridPosition } from '../../types';
import { ConnectionStatus } from '../../types';
import type { TileStackView } from '../board/TileStackView';
import { KnotBlockerLayout } from '../../../../../layouts/blockers/KnotBlockerLayout';
import { soundManager } from '../../../../app/SoundManager';
import { TILE_HEIGHT } from '../board/TileView';

const REMOVAL_ANIMATION_DURATION = 300;
const REMOVAL_ANIMATION_DELAY = 300;

const KNOT_REMOVE_ANIMATION_DURATION = 300;

const ROPE_VIBRATE_ANIMATION_DURATION = 600;
const ROPE_RETRACT_ANIMATION_DURATION: number = BOARD_SETTINGS.timing.connectedBlockerRemovalDuration;
const ROPE_CUT_ANIMATION_DURATION: number = BOARD_SETTINGS.timing.connectedBlockerRemovalDuration;

type RopeContainer = Container & { ropeSprite: Sprite; ropeMask: Graphics };
type RopeConnection = { cell: GridPosition; container: RopeContainer };

export class KnotBlockerView extends Container implements ConnectedBlockerView {
  private readonly layout: KnotBlockerLayout;

  private popSoundLock = false;

  private readonly knotLoops: Sprite[] = [];

  private hp = 0;

  private ropeConnections: RopeConnection[] = [];

  constructor(
    private readonly model: KnotBlockerModel,
    private readonly stackView: TileStackView,
  ) {
    super();

    this.layout = new KnotBlockerLayout();
    this.layout.root.props({
      parent: this,
    });

    this.knotLoops = [this.layout.loop_01, this.layout.loop_02, this.layout.loop_03, this.layout.loop_04];

    this.setHp(model.getHp());
    this.updateConnections();
  }

  public getModel(): KnotBlockerModel {
    return this.model;
  }

  public destroy(options?: DestroyOptions): void {
    if (this.destroyed) return;

    super.destroy(options);
  }

  public onModelChanged(): void {
    if (this.hp - 1 === this.model.getHp()) {
      void this.animateHit();
    } else {
      this.setHp(this.model.getHp());
    }
  }

  public async animateRemoval(): Promise<void> {
    if (this.destroyed) return;

    const hitPromises = Array.from({ length: this.hp }, () => this.animateHit());

    void Promise.all([
      this.tweenTo(
        { alpha: 0 },
        REMOVAL_ANIMATION_DURATION * (2 / 3),
        'sinOut',
        true,
        REMOVAL_ANIMATION_DELAY,
      ).asyncOnComplete(),

      this.tweenTo(
        { scaleX: 0, scaleY: 0 },
        REMOVAL_ANIMATION_DURATION,
        'backIn',
        true,
        REMOVAL_ANIMATION_DELAY * (2 / 3),
      ).asyncOnComplete(),
    ]).then(() => {
      this.destroy({ children: true });
    });

    // fix for bug where stack is still lock after destroying rope blocker
    // when destroy is called during tweens, that tween is not completing
    const destroyPromise = new Promise<void>((resolve) => {
      if (this.destroyed) {
        resolve();
      } else {
        this.once('destroyed', () => {
          resolve();
        });
      }
    });

    await Promise.race([destroyPromise, Promise.all(hitPromises)]);
  }

  /**
   * Updates cell connections by recalculating positions and adding new rope sprites.
   * This method ensures that the ropes are correctly positioned and oriented
   * based on the current state of the connected cells.
   */
  public updateConnections(): void {
    this.layout.rope_layer.removeChildren();

    const cellPosition = gridPositionToPxPosition(this.stackView.model.x, this.stackView.model.y);

    let index = 0;

    this.model.connection.getAll().forEach((connectedCell) => {
      index += 1;

      const { x, y } = connectedCell;
      const connectedCellPosition = gridPositionToPxPosition(x, y);

      const direction = {
        x: connectedCellPosition.x - cellPosition.x,
        y: connectedCellPosition.y - cellPosition.y,
      } as XY;

      MathUtils.getUnitVector(direction.x, direction.y, direction);
      const distance = MathUtils.getDistance(
        cellPosition.x,
        cellPosition.y,
        connectedCellPosition.x,
        connectedCellPosition.y,
      );

      const angle = MathUtils.getVectorAngle(direction.x, direction.y);

      const rope = new Sprite({ label: 'rope' }).props({
        texture: 'knot_rope',
        rotation: angle,
        pivot: { x: 10, y: 8 },
        scaleX: distance / TILE_HEIGHT,
      });

      // Create a mask for the rope
      const mask = new Graphics({ label: 'rope_mask' });
      mask.rect(0, 0, rope.width, rope.height * 4).fill(0xffffff);
      mask.props({
        rotation: angle,
        pivot: { x: 11, y: rope.height * 2 },
      });

      rope.mask = mask;

      // Create a container for the rope and mask
      const container = new Container({ label: `rope_container_${String(index).padStart(2, '0')}` }) as RopeContainer;
      container.addChild(mask);
      container.addChild(rope);
      container.ropeSprite = rope;
      container.ropeMask = mask;

      // Add the container to the main rope container
      this.layout.rope_layer.addChild(container);

      this.ropeConnections.push({ cell: { x, y }, container });
    });
  }

  /**
   * Animates ropes (vibrating) based on hit origin.
   *
   * @param hitOrigin - The hit origin point.
   * @returns A promise that resolves when the animation is complete.
   */
  public async animateRopesOnHit(hitOrigin: GridPosition): Promise<void> {
    await waitForMs(KNOT_REMOVE_ANIMATION_DURATION / 2);

    return this.tweens
      .createValueTween(
        0,
        1,
        ROPE_VIBRATE_ANIMATION_DURATION,
        (value) => {
          if (this.model.connection.status !== ConnectionStatus.Connected) return;

          const intensity = 0.4 - value * 0.2;
          const offset = Math.sin(value * Math.PI * 2) * intensity;
          const connections = this.filterConnectionsToAnimate(hitOrigin);

          connections.forEach((connection) => {
            if (!connection.container.ropeSprite.anchor) return;

            if (this.model.connection.status !== ConnectionStatus.Connected) {
              connection.container.ropeSprite.anchor.y = 0;
              return;
            }

            connection.container.ropeSprite.anchor.y = this.isTarget(hitOrigin) ? offset : -offset;
          });
        },
        'backOut',
      )
      .asyncOnComplete();
  }

  public async animateCutRopes(cutOrigin: GridPosition, delay = 0): Promise<void> {
    const cutIndex = this.ropeConnections.findIndex(
      (connection) => connection.cell.x === cutOrigin.x && connection.cell.y === cutOrigin.y,
    );

    const [connectionToCut] = this.ropeConnections?.splice(cutIndex, 1) || [];

    return new Promise<void>((resolve) => {
      const executeAsyncAnimation = async (): Promise<void> => {
        await waitForMs(delay);

        if (!connectionToCut) {
          resolve();
          return;
        }

        await this.tweens
          .createValueTween(
            0,
            1,
            ROPE_CUT_ANIMATION_DURATION,
            (value) => {
              if (!connectionToCut.container.ropeSprite.anchor) {
                console.error('animateCutRope Rope sprite anchor is missing for connection', connectionToCut);
                return;
              }
              connectionToCut.container.ropeSprite.anchor.x = value;
            },
            'sinOut',
          )
          .asyncOnComplete();

        connectionToCut.container.destroy({ children: true });

        resolve();
      };

      void executeAsyncAnimation();
    });
  }

  public animateRetractRopes(retractOrigin: GridPosition, delay = 0): Promise<void> {
    const direction = this.isTarget(retractOrigin) ? -1 : 1;
    const connections = this.filterConnectionsToAnimate(retractOrigin);

    this.ropeConnections = this.ropeConnections.filter(
      (connection) => connection.cell.x !== retractOrigin.x || connection.cell.y !== retractOrigin.y,
    );

    return new Promise<void>((resolve) => {
      const executeAsyncAnimation = async (): Promise<void> => {
        await waitForMs(KNOT_REMOVE_ANIMATION_DURATION + delay);

        if (connections.length === 0) {
          resolve();
          return;
        }

        await this.tweens
          .createValueTween(
            0,
            direction,
            ROPE_RETRACT_ANIMATION_DURATION,
            (value) => {
              connections.forEach((connection) => {
                if (!connection.container.ropeSprite.anchor) {
                  console.warn('animateRetractRopes Rope sprite anchor is missing for connection', connection);
                  return;
                }
                connection.container.ropeSprite.anchor.x = value;
              });
            },
            'sinOut',
          )
          .asyncOnComplete();

        connections.forEach((connection) => {
          connection.container.destroy({ children: true });
        });

        resolve();
      };

      void executeAsyncAnimation();
    });
  }

  private setHp(hp: number): void {
    this.hp = Math.min(this.model.maxHp, Math.max(0, hp));

    const knotIndex = this.model.maxHp - this.hp;

    for (let i = 0; i < this.knotLoops.length; i += 1) {
      const knotLoop = this.knotLoops[i];
      knotLoop.visible = knotIndex <= i;
    }

    this.stackView.tintTiles(0xffffff);

    this.hp = hp;
  }

  private async animateHit(): Promise<void> {
    if (this.hp <= 0) return;
    const knotIndex = this.model.maxHp - this.hp;

    this.hp -= 1;

    const knot = this.knotLoops[knotIndex];

    // make a copy of the knot to animate it separately
    const animatedKnot = new Sprite().props({
      texture: knot.texture,
      anchor: knot.anchor,
      scale: knot.scale,
      position: knot.position,
    });

    const container = this.layout.knot_container;

    const childIndex = container.getChildIndex(this.knotLoops[knotIndex]);
    container.addChildAt(animatedKnot, childIndex);

    knot.visible = false;
    await container
      .tweenTo({ scaleY: 0.8, scaleX: 1.1 }, KNOT_REMOVE_ANIMATION_DURATION / 3, 'sinOut', true)
      .asyncOnComplete();
    const scaleOutPromise = animatedKnot
      .tweenTo({ scaleX: 0, scaleY: 0 }, KNOT_REMOVE_ANIMATION_DURATION, 'cubicOut', true)
      .asyncOnComplete();

    this.playLoopPopSound();

    await container
      .tweenTo({ scaleX: 0.8, scaleY: 1.1 }, KNOT_REMOVE_ANIMATION_DURATION / 3, 'sinOut', true)
      .asyncOnComplete();
    await container
      .tweenTo({ scaleX: 1, scaleY: 1 }, KNOT_REMOVE_ANIMATION_DURATION / 3, 'sinOut', true)
      .asyncOnComplete();

    await scaleOutPromise;
    animatedKnot.destroy({ children: true });
  }

  private playLoopPopSound(): void {
    if (!this.popSoundLock) {
      this.popSoundLock = true;
      soundManager.play('Gameplay_Knot_Pop', {
        volume: 0.05,
        complete: () => {
          this.popSoundLock = false;
        },
      });
    }
  }

  private filterConnectionsToAnimate(connectedCell: GridPosition): RopeConnection[] {
    if (this.isTarget(connectedCell)) {
      return this.ropeConnections;
    }

    return this.ropeConnections.filter(
      (connection) => connection.cell.x === connectedCell.x && connection.cell.y === connectedCell.y,
    );
  }

  private isTarget(cell: GridPosition): boolean {
    return this.stackView.model.x === cell.x && this.stackView.model.y === cell.y;
  }
}
